import { isEmpty } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { FlatList, StyleProp, ViewStyle } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { MBImagePreviewModal } from '@shared/components/image';
import { Pill } from '@shared/components/pill';
import { FileUpload } from '@shared/types/fieldValues';
import { useBoolean } from '@shared/util/hooks';
import { isImage } from '@shared/util/media/type';
import { ImageItem } from './ImageItem';

interface FilesProps {
  files: FileUpload[];
  editable: boolean;
  onFileRemoved?: (file: FileUpload) => void;
  style?: StyleProp<ViewStyle>;
  itemStyle?: StyleProp<ViewStyle>;
  maxWidth?: number;
  maxHeight?: number;
  maxImages?: number;
}

export function ImageList({
  files,
  onFileRemoved,
  editable,
  style,
  itemStyle,
  maxWidth = 200,
  maxHeight = 180,
  maxImages = Infinity,
}: FilesProps) {
  const [isPreviewOpen, showPreview, hidePreview] = useBoolean(false);
  const [showIndex, setShowIndex] = useState(0);

  const images = useMemo(() => files.filter(isImage), [files]);
  const imagesToShow = useMemo(() => images.slice(0, maxImages), [images, maxImages]);

  const onPressImage = useCallback(
    (index: number) => {
      setShowIndex(index);
      showPreview();
    },
    [showPreview],
  );

  if (isEmpty(images)) {
    return null;
  }

  return (
    <>
      <MBView row style={style}>
        <FlatList
          keyExtractor={item => item.url}
          data={imagesToShow}
          horizontal={true}
          renderItem={({ item, index }) => (
            <ImageItem
              image={item}
              editable={editable}
              maxWidth={maxWidth}
              maxHeight={maxHeight}
              style={itemStyle}
              onPress={() => onPressImage(index)}
              onClear={() => onFileRemoved?.(item)}
            />
          )}
          ListFooterComponent={
            <ImageListFooter images={images} maxImages={maxImages} onPressImage={onPressImage} />
          }
        />
      </MBView>
      {isPreviewOpen && (
        <MBImagePreviewModal
          images={images}
          showIndex={showIndex}
          visible={isPreviewOpen}
          onClose={hidePreview}
        />
      )}
    </>
  );
}

function ImageListFooter({
  images,
  maxImages,
  onPressImage,
}: {
  images: FileUpload[];
  maxImages: number;
  onPressImage: (index: number) => void;
}) {
  const extraImagesCount = images.length - maxImages;
  if (extraImagesCount < 1) {
    return null;
  }
  return <Pill noLeftMargin onPress={() => onPressImage(maxImages)}>{`+${extraImagesCount}`}</Pill>;
}
